"use client"

import { useState, useEffect } from "react"
import { Link, useSearchParams, useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeftLong, faArrowRightLong } from "@fortawesome/free-solid-svg-icons"
import { useQuery } from "@tanstack/react-query"

import CaseManager from "../../../styles/case-manager-modules/caseManagerDashboard.module.css"
import styles from "../../../styles/patient-page-modules/PatientDetail.module.css"
import Notes from "../../../components/patient-detail/notes"
import Readings from "../../../components/patient-detail/readings"
import MonitoringCard from "../../../components/shared-modules/monitoring-time-view/monitoring"
import Spinner from "../../../components/spinner"
import Reload from "../../../components/ui/reload-data"
import { getPatientGeneralDetails } from "../../../services/patient/details"

const STORAGE_KEYS = {
  PATIENT_SEQUENCE: "_ap",
  PATIENT_ID: "_pt_id",
  COMPLETION_PERCENTAGE: "completion_percentage",
  CURRENT_LOCATION: "current_loc",
  CCM: "ccm",
  RPM: "rpm",
}

const ReadingReview = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("tasks")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentWorkflow, setCurrentWorkflow] = useState(() => {
    return sessionStorage.getItem("_active_workflow") || "All Patients"
  })
  const [navigationState, setNavigationState] = useState({
    currentPatientIndex: 0,
    patientSequence: [],
    patientId: null,
    initialLoadComplete: false,
    completionPercentage: 0,
  })

  const [isSectionOpen, setIsSectionOpen] = useState({
    section_1: true,
    section_2: true,
    section_3: true,
    section_4: true,
    section_5: true,
    section_6: true,
    section_3_right: true,
  })

  const patientIdFromUrl = searchParams.get("patient_id")

  useEffect(() => {
    if (navigationState.initialLoadComplete) return

    const storedSequence = sessionStorage.getItem(STORAGE_KEYS.PATIENT_SEQUENCE)
    const storedPercentage = sessionStorage.getItem(STORAGE_KEYS.COMPLETION_PERCENTAGE)

    if (!storedSequence) {
      console.error("No patient sequence found in sessionStorage")
      return
    }

    try {
      const parsedSequence = JSON.parse(storedSequence)
      const completionPercentage = storedPercentage ? parseInt(storedPercentage) : 0

      setNavigationState(prev => ({
        ...prev,
        patientSequence: parsedSequence,
        completionPercentage,
      }))

      if (parsedSequence.length > 0) {
        if (!patientIdFromUrl) {
          const firstPatientId = parsedSequence[0]
          const ccm = sessionStorage.getItem(STORAGE_KEYS.CCM) === "true" ? 1 : 0
          const rpm = sessionStorage.getItem(STORAGE_KEYS.RPM) === "true" ? 1 : 0

          navigate(`/admin-case-manager/reading-reviews?patient_id=${firstPatientId}&CCM=${ccm}&RPM=${rpm}`, {
            replace: true,
          })
        } else {
          setNavigationState(prev => ({
            ...prev,
            patientId: patientIdFromUrl,
            currentPatientIndex: parsedSequence.includes(patientIdFromUrl) 
              ? parsedSequence.indexOf(patientIdFromUrl) 
              : 0,
            initialLoadComplete: true,
          }))
          sessionStorage.setItem(STORAGE_KEYS.PATIENT_ID, patientIdFromUrl)
        }
      }
    } catch (error) {
      console.error("Error parsing patient sequence:", error)
    }
  }, [patientIdFromUrl, navigate, navigationState.initialLoadComplete])

  useEffect(() => {
    if (navigationState.initialLoadComplete && patientIdFromUrl) {
      setNavigationState(prev => ({
        ...prev,
        patientId: patientIdFromUrl,
        currentPatientIndex: prev.patientSequence.includes(patientIdFromUrl)
          ? prev.patientSequence.indexOf(patientIdFromUrl)
          : prev.currentPatientIndex,
      }))
    }
  }, [patientIdFromUrl, navigationState.initialLoadComplete])

  const {
    data: patient,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["patient-details", navigationState.patientId],
    queryFn: () => getPatientGeneralDetails(navigationState.patientId),
    enabled: !!navigationState.patientId,
    staleTime: 5 * 60 * 1000, 
    cacheTime: 10 * 60 * 1000, 
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const locationInfo = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.CURRENT_LOCATION) || "{}")
  const locationName = locationInfo?.name || "Unknown Location"
  const isFirstPatient = navigationState.currentPatientIndex <= 0
  const isLastPatient = navigationState.currentPatientIndex >= navigationState.patientSequence.length - 1

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  const toggleSection = (section) => {
    setIsSectionOpen((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }))
  }

  const navigateToPatient = (patientId, ccm = 0, rpm = 0) => {
    sessionStorage.setItem(STORAGE_KEYS.PATIENT_ID, patientId)
    navigate(`/admin-case-manager/reading-reviews?patient_id=${patientId}&CCM=${ccm}&RPM=${rpm}`)
  }

  const goToPreviousPatient = () => {
    if (!isFirstPatient) {
      const prevIndex = navigationState.currentPatientIndex - 1
      const prevPatientId = navigationState.patientSequence[prevIndex]
      const ccm = patient?.ccm ? 1 : 0
      const rpm = patient?.rpm ? 1 : 0
      navigateToPatient(prevPatientId, ccm, rpm)
    }
  }

  const goToNextPatient = () => {
    if (!isLastPatient) {
      const nextIndex = navigationState.currentPatientIndex + 1
      const nextPatientId = navigationState.patientSequence[nextIndex]
      const ccm = patient?.ccm ? 1 : 0
      const rpm = patient?.rpm ? 1 : 0
      navigateToPatient(nextPatientId, ccm, rpm)
    }
  }

  const tabContent = {
    tasks: (
      <>
        <div className={CaseManager.section_2}>
          <Readings CaseManager={true} />
        </div>
        <div className={CaseManager.section_2}>
          <div className={CaseManager.section_3_right}>
            <Notes isOpen={isSectionOpen.section_3_right} onClose={() => toggleSection("section_3_right")} />
          </div>
          <div className={CaseManager.section_3_left}>
            <MonitoringCard noteType="rpm_note" caseManager={true} />
          </div>
        </div>
      </>
    ),
  }

  if (isLoading || !navigationState.patientId || !navigationState.initialLoadComplete)
    return (
      <div className={styles.patient_page}>
        <div className={styles.patient_page_container}>
          <div className={styles.spinner_container}>
            <Spinner />
          </div>
        </div>
      </div>
    )

  if (error)
    return (
      <div className={styles.patient_page}>
        <div className={styles.patient_page_container}>
          <Reload message="Could not load patient details" onReload={refetch} />
        </div>
      </div>
    )

  const patientName = patient?.user ? `${patient.user.first_name} ${patient.user.last_name}` : "Patient"

  return (
    <div className={styles.patient_page}>
      <div className={styles.name_container_spacer}></div>
      <div className={styles.name_container}>
        <div className={styles.name_wrapper}>
          <Link
            to="/admin-case-manager"
            className={styles.back_btn}
            aria-label="Back"
            title="Back"
          >
            <FontAwesomeIcon icon={faArrowLeftLong} />
          </Link>
          <div className={styles.name_and_status}>
            <h4>
              <strong>{patientName}    <span className={styles.workflow_tag}>Reading Review Workflow   </span>
              </strong> 
            </h4>
          </div>
        </div>
      </div>

      <div className={styles.patient_page_container}>
        <div className={styles.tabs_container}>
          <button
            className={`${styles.nav_button} ${isFirstPatient ? styles.disabled_button : ""}`}
            onClick={goToPreviousPatient}
            disabled={isFirstPatient}
            style={isFirstPatient ? { opacity: 0.5, cursor: "not-allowed" } : {}}
          >
            <FontAwesomeIcon icon={faArrowLeftLong} /> Prev Patient
          </button>

          <button
            className={`${styles.nav_button} ${isLastPatient ? styles.disabled_button : ""}`}
            onClick={goToNextPatient}
            disabled={isLastPatient}
            style={isLastPatient ? { opacity: 0.5, cursor: "not-allowed" } : {}}
          >
            Next Patient <FontAwesomeIcon icon={faArrowRightLong} />
          </button>
        </div>
        <div className={styles.tab_content}>{tabContent[activeTab]}</div>
      </div>
    </div>
  )
}

export default ReadingReview